/**
 * HydrationOrchestrator.ts — OPERATOR IDENTITY™ Phase 3 (Build 2.08.0)
 *
 * The single deterministic cloud-restoration pipeline. Runs after boot and
 * restores the operator's complete workspace in a fixed order:
 *
 *   1. Operator Profile   2. Settings (config + header + UI state)
 *   3. Backpack™          4. Vault™          5. Career™
 *   6. Wallet™            7. Passport™       8. Visa™
 *   9. Stay™             10. Planner™
 *  11. Marketplace™      12. Connect™       13. Health™
 *  14. Guided Intros     15. Network™       16. Academy™
 *
 * Guarantees:
 *   • Hydration failures NEVER stop boot — each step is independently guarded.
 *   • Fire-and-forget: runFullHydration() is idempotent per boot; concurrent
 *     callers share the in-flight promise.
 *   • Publishes OperatorHydrated (after the profile step) and
 *     CloudHydrationCompleted (after all steps) on the contracted Core bus.
 *   • After hydration, the persona's currentBase is reconciled with the
 *     RoamerLocationEngine — publishing only the EXISTING LocationChanged
 *     event (which cascades weather / currency / Roamer Score refreshes).
 *
 * SESSION CONTINUITY™ — also owns the "continue where you left off" snapshot:
 * last route + key UI-state localStorage blobs, cloud-synced under
 * operator/ui-state so a second device resumes exactly where the first left.
 */

import { storageManager } from '@/services/storage/StorageManager';
import { roamerCore } from '@/services/core';
import { loadPersona, hydratePersonaFromCloud } from '@/services/OperatorPersonaService';
import { hydrateOperatorConfigFromCloud } from '@/lib/operatorConfig';
import { hydrateHeaderCustomizationFromCloud } from '@/services/HeaderCustomizationService';
import { hydrateBackpackFromCloud } from '@/services/RoamerBackpackService';
import { hydrateVaultFromCloud } from '@/services/RoamerVaultService';
import { hydrateWalletFromCloud } from '@/services/wallet/RoamerWalletModule';
import { hydratePassportFromCloud } from '@/services/passport/RoamerPassportModule';
import { hydrateVisaFromCloud } from '@/services/visa/RoamerVisaModule';
import { hydrateStayFromCloud } from '@/services/stay/RoamerStayModule';
import { hydrateMarketplaceFromCloud } from '@/services/marketplace/RoamerMarketplaceModule';
import { hydrateConnectFromCloud } from '@/services/connect/RoamerConnectModule';
import { hydrateHealthFromCloud } from '@/services/health/RoamerHealthModule';
import { hydrateWorkspaceIntrosFromCloud } from '@/services/personalization/WorkspaceIntroService';
import { hydrateCareerFromCloud } from '@/services/career/RoamerCareerModule';
import { hydratePlannerFromCloud } from '@/services/planner/RoamerPlannerModule';
import { hydrateNetworkFromCloud } from '@/services/network/RoamerNetworkModule';
import { hydrateAcademyFromCloud } from '@/academy/AcademyProgressService';
import { roamerLocationEngine } from '@/services/RoamerLocationEngine';

// ─── Types ────────────────────────────────────────────────────────────────────

export type HydrationStepStatus =
  | 'pending'   // not started yet
  | 'running'   // in flight
  | 'restored'  // cloud copy adopted
  | 'no-data'   // cloud reachable but nothing newer / nothing stored
  | 'skipped'   // module has no cloud persistence (local-only by design)
  | 'failed';   // hydrate call threw

export interface HydrationStep {
  id: string;
  label: string;
  status: HydrationStepStatus;
  durationMs: number | null;
  error: string | null;
}

export interface HydrationReport {
  startedAt: string | null;
  completedAt: string | null;
  durationMs: number | null;
  steps: HydrationStep[];
  restored: number;
  failed: number;
  skipped: number;
  noData: number;
  /** 'cloud' — at least one cloud adoption; 'local' — cache only; 'offline' — cloud unreachable. */
  source: 'cloud' | 'local' | 'offline' | null;
  running: boolean;
}

type Listener = (report: HydrationReport) => void;

interface StepDef {
  id: string;
  label: string;
  /** null = local-only module: recorded as 'skipped'. */
  run: (() => Promise<boolean>) | null;
}

// ─── Session Continuity™ ──────────────────────────────────────────────────────

import { getDeviceId } from '@/services/DeviceIdentityService';

const CONTINUITY_KEY = 'nomad:session-continuity:v1';
/** Local UI-state keys mirrored to the cloud so a second device resumes seamlessly. */
const MIRRORED_UI_KEYS = ['nomad-appearance-v1', 'nomad-mc-state-v1'] as const;

export interface ContinuitySnapshot {
  lastRoute: string;
  ui: Record<string, string>;
  updatedAt: string;
  /** Phase 5 (2.10.0): scroll offset of the main pane on lastRoute. */
  scrollY?: number;
  /** Phase 5 (2.10.0): which device wrote this snapshot (diagnostics only). */
  deviceId?: string;
}

function readContinuity(): ContinuitySnapshot | null {
  try {
    const raw = localStorage.getItem(CONTINUITY_KEY);
    return raw ? (JSON.parse(raw) as ContinuitySnapshot) : null;
  } catch { return null; }
}

/**
 * Record the operator's current route. Called by Shell on navigation.
 * Persists locally on every call; pushes to cloud (debounced by the
 * StorageManager write-through queue).
 */
export function recordLastRoute(path: string, scrollY?: number): void {
  // Never resume into auth/dev bootstrap routes.
  if (path.startsWith('/login') || path.startsWith('/signup') || path.startsWith('/forgot')) return;
  try {
    const ui: Record<string, string> = {};
    for (const key of MIRRORED_UI_KEYS) {
      const v = localStorage.getItem(key);
      if (v !== null) ui[key] = v;
    }
    const prev = readContinuity();
    const snap: ContinuitySnapshot = {
      lastRoute: path,
      ui,
      updatedAt: new Date().toISOString(),
      scrollY: scrollY ?? (prev?.lastRoute === path ? prev.scrollY : 0),
      deviceId: getDeviceId(),
    };
    localStorage.setItem(CONTINUITY_KEY, JSON.stringify(snap));
    storageManager.push('operator', 'ui-state', snap);
  } catch { /* best-effort */ }
}

/** The route to resume into, or null when none recorded. */
export function getLastRoute(): string | null {
  return readContinuity()?.lastRoute ?? null;
}

/** Full continuity snapshot (route + scroll + device) — diagnostics + resume. */
export function getContinuitySnapshot(): ContinuitySnapshot | null {
  return readContinuity();
}

// ── Continue Where You Left Off™ ─────────────────────────────────────────────
// The resume target is consumed at most ONCE per boot: after the Welcome Back
// screen the Shell navigates to it and restores scroll. Subsequent calls in
// the same tab session return null so in-app navigation is never hijacked.

const RESUME_CONSUMED_KEY = 'wr-resume-consumed';

export interface ResumeTarget { route: string; scrollY: number; }

export function consumeResumeTarget(): ResumeTarget | null {
  try {
    if (sessionStorage.getItem(RESUME_CONSUMED_KEY)) return null;
    sessionStorage.setItem(RESUME_CONSUMED_KEY, '1');
  } catch { return null; }
  const snap = readContinuity();
  if (!snap?.lastRoute) return null;
  return { route: snap.lastRoute, scrollY: snap.scrollY ?? 0 };
}

/** Diagnostics/testing: allow the resume target to be consumed again. */
export function resetResumeConsumption(): void {
  try { sessionStorage.removeItem(RESUME_CONSUMED_KEY); } catch { /* ignore */ }
}

/** Adopt a cloud continuity snapshot (restores mirrored UI-state keys). */
async function hydrateContinuityFromCloud(): Promise<boolean> {
  const cloud = await storageManager.hydrate<ContinuitySnapshot>('operator', 'ui-state');
  if (!cloud || typeof cloud !== 'object') return false;
  try {
    localStorage.setItem(CONTINUITY_KEY, JSON.stringify(cloud));
    for (const [key, value] of Object.entries(cloud.ui ?? {})) {
      if ((MIRRORED_UI_KEYS as readonly string[]).includes(key) && typeof value === 'string') {
        localStorage.setItem(key, value);
      }
    }
    return true;
  } catch { return false; }
}

// ─── Orchestrator ─────────────────────────────────────────────────────────────

const STEP_DEFS: StepDef[] = [
  { id: 'profile',     label: 'Operator Profile', run: hydratePersonaFromCloud },
  { id: 'settings',    label: 'Settings',         run: async () => {
      const results = await Promise.allSettled([
        hydrateOperatorConfigFromCloud(),
        hydrateHeaderCustomizationFromCloud(),
        hydrateContinuityFromCloud(),
      ]);
      return results.some(r => r.status === 'fulfilled' && r.value === true);
    } },
  { id: 'backpack',    label: 'Backpack™',        run: hydrateBackpackFromCloud },
  { id: 'vault',       label: 'Vault™',           run: hydrateVaultFromCloud },
  { id: 'career',      label: 'Career™',          run: hydrateCareerFromCloud },
  { id: 'wallet',      label: 'Wallet™',          run: hydrateWalletFromCloud },
  { id: 'passport',    label: 'Passport™',        run: hydratePassportFromCloud },
  { id: 'visa',        label: 'Visa™',            run: hydrateVisaFromCloud },
  { id: 'stay',        label: 'Stay™',            run: hydrateStayFromCloud },
  { id: 'planner',     label: 'Planner™',         run: hydratePlannerFromCloud },
  { id: 'marketplace', label: 'Marketplace™',     run: hydrateMarketplaceFromCloud },
  { id: 'connect',     label: 'Connect™',         run: hydrateConnectFromCloud },
  { id: 'health',      label: 'Health™',          run: hydrateHealthFromCloud },
  { id: 'workspace-intros', label: 'Guided Intros', run: hydrateWorkspaceIntrosFromCloud },
  { id: 'network',     label: 'Network™',         run: hydrateNetworkFromCloud },
  { id: 'academy',     label: 'Academy™',         run: hydrateAcademyFromCloud },
];

/** Labels of steps with real cloud persistence (local-only steps excluded). */
export function getCloudSyncedStepLabels(): string[] {
  return STEP_DEFS.filter(d => d.run !== null).map(d => d.label);
}

class HydrationOrchestrator {
  private steps: HydrationStep[] = STEP_DEFS.map(d => ({
    id: d.id, label: d.label, status: 'pending', durationMs: null, error: null,
  }));
  private startedAt: string | null = null;
  private completedAt: string | null = null;
  private durationMs: number | null = null;
  private source: HydrationReport['source'] = null;
  private inFlight: Promise<HydrationReport> | null = null;
  private listeners = new Set<Listener>();

  // ── Public API ─────────────────────────────────────────────────────────────

  /** Subscribe to progress. Returns an unsubscribe function. */
  subscribe(cb: Listener): () => void {
    this.listeners.add(cb);
    return () => this.listeners.delete(cb);
  }

  getReport(): HydrationReport {
    const counts = { restored: 0, failed: 0, skipped: 0, noData: 0 };
    for (const s of this.steps) {
      if (s.status === 'restored') counts.restored++;
      else if (s.status === 'failed') counts.failed++;
      else if (s.status === 'skipped') counts.skipped++;
      else if (s.status === 'no-data') counts.noData++;
    }
    return {
      startedAt: this.startedAt,
      completedAt: this.completedAt,
      durationMs: this.durationMs,
      steps: this.steps.map(s => ({ ...s })),
      ...counts,
      source: this.source,
      running: this.inFlight !== null && this.completedAt === null,
    };
  }

  /**
   * Run the full deterministic hydration pipeline.
   * Idempotent per boot — concurrent/repeat callers share one run unless
   * force=true (Developer Center "Force Hydrate").
   */
  runFullHydration(options?: { force?: boolean }): Promise<HydrationReport> {
    if (this.inFlight && !options?.force) return this.inFlight;
    if (this.inFlight && options?.force && this.completedAt === null) return this.inFlight;

    // (Re)initialise state for a fresh run.
    this.steps = STEP_DEFS.map(d => ({
      id: d.id, label: d.label, status: 'pending', durationMs: null, error: null,
    }));
    this.startedAt = new Date().toISOString();
    this.completedAt = null;
    this.durationMs = null;
    this.source = null;

    this.inFlight = this.execute();
    return this.inFlight;
  }

  /**
   * Resolve once the PROFILE hydration step has reached a terminal state
   * (restored / no-data / failed) — or after `timeoutMs`, whichever is first.
   * Starts the pipeline if nothing has kicked it yet. Used by Shell so the
   * onboarding-vs-welcome decision waits for the cloud persona on a fresh
   * device instead of forcing a returning operator back into onboarding.
   */
  waitForProfileHydration(timeoutMs = 4000): Promise<void> {
    const isTerminal = () => {
      const s = this.steps.find(st => st.id === 'profile');
      return !!s && (s.status === 'restored' || s.status === 'no-data' || s.status === 'failed' || s.status === 'skipped');
    };

    // ── Fast path: profile already successfully restored (or permanently skipped) ─
    const profileStep = this.steps.find(st => st.id === 'profile');
    if (profileStep?.status === 'restored' || profileStep?.status === 'skipped') {
      return Promise.resolve();
    }

    // ── Re-run path: previous attempt got no-data or failed ──────────────────
    // This happens when bootstrap.ts fires runFullHydration() at module-init
    // time — before SupabaseAuthContext has registered the Bearer-token getter.
    // All /api/persistence calls return 401, profile step ends up as 'no-data'
    // or 'failed', and the HydrationOrchestrator marks itself done. When Shell
    // later calls waitForProfileHydration() (after auth is confirmed via apiUser),
    // the old isTerminal() guard would resolve immediately with stale empty data.
    //
    // Fix: if the previous attempt ended without a successful cloud restore,
    // force a fresh run — this time the Bearer token IS available.
    if (profileStep?.status === 'no-data' || profileStep?.status === 'failed') {
      void this.runFullHydration({ force: true });
    } else {
      // Either pending/running (first call) or no in-flight yet — start normally.
      void this.runFullHydration();
    }

    if (isTerminal()) return Promise.resolve();
    return new Promise<void>(resolve => {
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        clearTimeout(timer);
        unsub();
        resolve();
      };
      const unsub = this.subscribe(() => { if (isTerminal()) finish(); });
      const timer = setTimeout(finish, timeoutMs);
    });
  }

  /** Testing hook — reset all state so tests can run isolated pipelines. */
  resetForTests(): void {
    this.inFlight = null;
    this.startedAt = null;
    this.completedAt = null;
    this.durationMs = null;
    this.source = null;
    this.steps = STEP_DEFS.map(d => ({
      id: d.id, label: d.label, status: 'pending', durationMs: null, error: null,
    }));
  }

  // ── Pipeline ───────────────────────────────────────────────────────────────

  private async execute(): Promise<HydrationReport> {
    const t0 = Date.now();

    for (let i = 0; i < STEP_DEFS.length; i++) {
      const def = STEP_DEFS[i];
      const step = this.steps[i];

      if (!def.run) {
        step.status = 'skipped';
        step.durationMs = 0;
        this.notify();
        continue;
      }

      step.status = 'running';
      this.notify();
      const s0 = Date.now();
      try {
        const adopted = await def.run();
        step.status = adopted ? 'restored' : 'no-data';
      } catch (err) {
        // Hydration failures must never stop boot.
        step.status = 'failed';
        step.error = err instanceof Error ? err.message : String(err);
      }
      step.durationMs = Date.now() - s0;
      this.notify();

      // OperatorHydrated fires right after the profile step so downstream
      // consumers (Morning Brief, Shell) can personalise immediately.
      if (def.id === 'profile') {
        const personaAfterProfile = loadPersona();
        console.log(
          `[NOMAD_DEBUG] HydrationOrchestrator: profile step DONE ` +
          `status=${step.status} ` +
          `durationMs=${step.durationMs ?? 0} ` +
          `persona=${personaAfterProfile ? JSON.stringify({
            displayName:        personaAfterProfile.displayName,
            onboardingCompleted: personaAfterProfile.onboardingCompleted,
            currentBase:        personaAfterProfile.currentBase?.city ?? null,
            homeBase:           personaAfterProfile.homeBase?.city ?? null,
            country:            personaAfterProfile.country,
            createdAt:          personaAfterProfile.createdAt,
          }) : 'null (nothing in localStorage)'}`,
        );
        this.publishOperatorHydrated();
      }
    }

    this.durationMs = Date.now() - t0;
    this.completedAt = new Date().toISOString();
    this.source = this.resolveSource();
    this.publishCompleted();
    this.reconcileCurrentBase();
    this.notify();
    return this.getReport();
  }

  private resolveSource(): HydrationReport['source'] {
    try {
      const diag = storageManager.getDiagnostics();
      if (diag.syncHealth === 'offline') return 'offline';
    } catch { /* diagnostics unavailable — fall through */ }
    return this.steps.some(s => s.status === 'restored') ? 'cloud' : 'local';
  }

  // ── Post-hydration: Current Base Intelligence ──────────────────────────────

  /**
   * Reconcile the hydrated persona's currentBase with the location engine.
   * Only publishes the EXISTING LocationChanged event (via setCurrentLocation),
   * which already cascades weather / currency / Roamer Score / internet /
   * cost-of-living / photos / timezone refreshes. No duplicate events.
   */
  private reconcileCurrentBase(): void {
    try {
      const persona = loadPersona();
      const base = persona?.currentBase;
      if (!base?.locationId) return;
      const current = roamerLocationEngine.getCurrentLocation();
      if (current?.locationId === base.locationId) return; // already in sync
      roamerLocationEngine.setCurrentLocation({
        locationId:       base.locationId,
        city:             base.city,
        region:           base.region,
        state:            base.state,
        country:          base.country,
        countryCode:      base.countryCode,
        latitude:         base.latitude,
        longitude:        base.longitude,
        timezone:         base.timezone,
        formattedAddress: base.formattedAddress,
        kind:             'city',
        lastUpdated:      Date.now(),
        source:           'manual',
      } as Parameters<typeof roamerLocationEngine.setCurrentLocation>[0], 'manual');
    } catch { /* best-effort — never break boot */ }
  }

  // ── Bus events ─────────────────────────────────────────────────────────────

  private publishOperatorHydrated(): void {
    try {
      const persona = loadPersona();
      const payload: Record<string, unknown> = {
        hasPersona: persona !== null,
        onboardingCompleted: persona?.onboardingCompleted ?? false,
      };
      if (persona?.displayName) payload.operatorName = persona.displayName;
      if (persona?.personaVersion) payload.personaVersion = persona.personaVersion;
      roamerCore.publish('OperatorHydrated', 'hydration-orchestrator', payload, Date.now());
    } catch { /* bus unavailable in tests — best-effort */ }
  }

  private publishCompleted(): void {
    try {
      const r = this.getReport();
      roamerCore.publish('CloudHydrationCompleted', 'hydration-orchestrator', {
        restored:   r.restored,
        failed:     r.failed,
        skipped:    r.skipped,
        noData:     r.noData,
        durationMs: this.durationMs ?? 0,
        source:     this.source ?? 'local',
      }, Date.now());
    } catch { /* best-effort */ }
  }

  private notify(): void {
    const report = this.getReport();
    for (const cb of this.listeners) {
      try { cb(report); } catch { /* listener errors never break the pipeline */ }
    }
  }
}

export const hydrationOrchestrator = new HydrationOrchestrator();
